import * as React from 'react'
import { CheckIcon } from '@/src/icons'
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// Checkbox — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Base UI Checkbox renders a <span> + hidden <input> (not a <button>).
//
// Data attribute changes:
//   data-state="checked"       → data-checked
//   data-state="unchecked"     → data-unchecked
//   data-state="indeterminate" → data-indeterminate
//
// onCheckedChange: Radix `(checked: boolean | 'indeterminate') => void`
//                → Base UI `(checked: boolean, eventDetails) => void`
// We wrap to preserve the Radix-compatible single-arg signature.
// ---------------------------------------------------------------------------

// DS Checkbox spec:
//   Control: size-4 (16px), rounded-[4px], border-input at rest
//   Checked: border-primary, bg-primary (purple-500), text-primary-foreground (white tick)
//   Hover:   subtle bg tint on the track area (handled by parent/label)
//   Focused: ring-[3px] ring-ring/50 (purple-300 at 50% opacity)
//   Error:   border-destructive, ring-destructive/20
//   Disabled: opacity-50, cursor-not-allowed
//   Indeterminate: rendered as checked state with minus icon by consuming code

export namespace Checkbox {
  export interface Props extends Omit<
    React.ComponentProps<typeof CheckboxPrimitive.Root>,
    'onCheckedChange' | 'render'
  > {
    /** Callback fired when the checked state changes. */
    onCheckedChange?: (checked: boolean) => void
  }
}

function Checkbox({ className, onCheckedChange, ...props }: Checkbox.Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'peer size-4 shrink-0 rounded-[4px] border border-input shadow-xs transition-[color,box-shadow] outline-none',
        'hover:border-ring/60',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        'data-[checked]:border-primary data-[checked]:bg-primary data-[checked]:text-primary-foreground',
        'data-[indeterminate]:border-primary data-[indeterminate]:bg-primary data-[indeterminate]:text-primary-foreground',
        'dark:bg-input/30 dark:data-[checked]:bg-primary dark:data-[indeterminate]:bg-primary',
        className,
      )}
      onCheckedChange={onCheckedChange ? (checked) => onCheckedChange(checked) : undefined}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none"
        keepMounted
      >
        <CheckIcon className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
